// Concierge SMS/MMS thread panel: recipient selector (athlete + guardians;
// the concierge owner is always included; optional covering-staff cell), a
// chat-style thread view, a composer, and light polling while a thread is
// open.
// File: frontend/src/components/concierge/SmsThreadPanel.js
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { relTime, fmtMonDay, toDate } from '../../lib/conciergeActivityModel';
import {
  guardianCheckboxes, defaultIncludeCell, buildStartPayload, buildFollowUpPayload, athletePhone,
  hasStartRecipient, ownerInfo, showIncludeCell, rosterLabel, formatPhone,
} from '../../lib/smsThreadModel';

const POLL_MS = 8000;

export default function SmsThreadPanel({ userId, athleteName, profile, cfg, API_URL, onSent, onScheduling }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // full thread { id, messages, ... } or null
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [includeAthlete, setIncludeAthlete] = useState(true);
  const [includeCell, setIncludeCell] = useState(false);
  const [fileName, setFileName] = useState('');
  const [mediaUrl, setMediaUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [guardians, setGuardians] = useState([]);
  const [recip, setRecip] = useState(null);
  const [recipError, setRecipError] = useState(false);
  // Seed from the profile prop so the checkbox is correct on first paint; the
  // backend /sms/recipients response (which also honors the User.phone
  // fallback + E.164 validity) refines it once loaded.
  const [athleteHasPhone, setAthleteHasPhone] = useState(!!athletePhone(profile));

  const owner = ownerInfo(recip);
  const includeCellVisible = showIncludeCell(recip);
  const anyGuardian = selectedSlots.length > 0;
  // "Start text" enables exactly when the POST would resolve a recipient AND
  // the concierge owner (always in the group) can actually be texted.
  const canStart = hasStartRecipient({ includeAthlete, athleteHasPhone, guardianSlots: selectedSlots }) && owner.textable;
  // A group closed on an owner change lacks the new owner; the backend 409s
  // any send into it, so the composer is off and the next text starts fresh.
  const activeClosed = active?.status === 'closed';
  const composerOff = active ? activeClosed : !canStart;

  useEffect(() => { setIncludeCell(includeCellVisible && defaultIncludeCell(anyGuardian)); }, [anyGuardian, includeCellVisible]);

  // Guardian checkboxes must mirror who the backend can actually text: it
  // resolves guardians primarily from the relational AthleteGuardian model
  // (falling back to the legacy profile.guardianN_phone columns), so the
  // selector fetches the same resolution from the backend rather than
  // deriving it from `profile` directly.
  useEffect(() => {
    axios.get(`${API_URL}/concierge/${userId}/sms/recipients`, cfg)
      .then(res => {
        setGuardians(guardianCheckboxes(res.data));
        setRecip(res.data);
        setAthleteHasPhone(!!res.data?.athlete_has_phone);
      })
      .catch(() => { setRecipError(true); });  }, [API_URL, userId]);

  // `cfg` is a fresh object literal on every render of the parent page, so
  // it's deliberately excluded from these deps (its contents — the bearer
  // token — are effectively stable for the component's lifetime). Including
  // it would re-run every effect below on every unrelated page re-render
  // (e.g. the page's "Saved Ns ago" ticker).
  const loadThreads = useCallback(() => {
    axios.get(`${API_URL}/concierge/${userId}/sms/threads`, cfg)
      .then(res => setThreads(Array.isArray(res.data.threads) ? res.data.threads : []))
      .catch(() => {});  }, [API_URL, userId]);

  const loadThread = useCallback((threadId) => {
    axios.get(`${API_URL}/concierge/${userId}/sms/threads/${threadId}`, cfg)
      .then(res => setActive(res.data.thread))
      .catch(() => {});  }, [API_URL, userId]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // Poll the open thread for inbound replies.
  const activeId = active?.id;
  useEffect(() => {
    if (!activeId) return undefined;
    const id = setInterval(() => loadThread(activeId), POLL_MS);
    return () => clearInterval(id);
  }, [activeId, loadThread]);

  const toggleSlot = (slot) => {
    setSelectedSlots(prev => (prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot]));
  };

  const clearFile = () => { setFileName(''); setMediaUrl(null); };

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!f) return;
    setFileName(f.name);
    setMediaUrl(null);
    setUploading(true);
    setErr(null);
    const form = new FormData();
    form.append('file', f);
    axios.post(`${API_URL}/concierge/${userId}/sms/upload`, form, cfg)
      .then(res => setMediaUrl(res.data.url))
      .catch(e => { setErr(e.response?.data?.detail || 'Failed to upload image.'); setFileName(''); })
      .finally(() => setUploading(false));
  };

  const startOrSend = () => {
    const body = draft.trim();
    if (!body && !mediaUrl) return;
    setSending(true);
    setErr(null);
    if (active) {
      const payload = buildFollowUpPayload({ body, mediaUrl });
      axios.post(`${API_URL}/concierge/${userId}/sms/threads/${active.id}/messages`, payload, cfg)
        .then(() => { setDraft(''); clearFile(); loadThread(active.id); loadThreads(); if (onSent) onSent(); })
        .catch(e => setErr(e.response?.data?.detail || 'Failed to send.'))
        .finally(() => setSending(false));
    } else {
      const payload = buildStartPayload({
        includeAthlete: includeAthlete && athleteHasPhone, guardianSlots: selectedSlots,
        includeCell, body, mediaUrl,
      });
      axios.post(`${API_URL}/concierge/${userId}/sms/threads`, payload, cfg)
        .then(res => { setActive(res.data.thread); setDraft(''); clearFile(); loadThreads(); if (onSent) onSent(); })
        .catch(e => setErr(e.response?.data?.detail || 'Failed to send.'))
        .finally(() => setSending(false));
    }
  };

  const now = new Date();
  const preview = (t) => {
    const m = t.last_message;
    if (!m) return '';
    const text = m.body || (m.has_media ? '📎 Image' : '');
    return m.sender_name ? `${m.sender_name}: ${text}` : text;
  };
  const timeOf = (iso) => {
    const d = toDate(iso);
    return d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
  };
  return (
    <section className="cn-card cn-texts">
      <div className="cn-texts__list">
        <div className="cn-card__head">
          <h2 className="cn-card__title">Texts</h2>
          <button type="button" className="cn-dbtn" onClick={() => setActive(null)}>+ New text</button>
        </div>
        {threads.length === 0 && <div className="cn-empty-line">No texts yet.</div>}
        <ul className="cn-texts__items">
          {threads.map(t => (
            <li key={t.id}>
              <button type="button" aria-current={active?.id === t.id ? 'true' : undefined}
                className={`cn-texts__item${active?.id === t.id ? ' cn-texts__item--on' : ''}`}
                onClick={() => loadThread(t.id)}>
                <span className="cn-texts__who">{rosterLabel(t)}</span>
                <span className="cn-texts__when">{relTime(t.last_message_at, now)}</span>
                <span className="cn-texts__preview">{preview(t)}</span>
                {t.status === 'closed' && <span className="cn-texts__closed">Closed — owner changed</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="cn-texts__convo">
        {active ? (
          <>
            <div className="cn-texts__head">
              <div>
                <div className="cn-texts__title">{rosterLabel(active)}</div>
                <div className="cn-texts__phones">
                  {(active.participants || []).filter(p => p.phone && p.role !== 'owner' && p.role !== 'staff')
                    .map(p => `${p.name || p.role} ${formatPhone(p.phone)}`).join(' · ')}
                </div>
              </div>
              {onScheduling && <button type="button" className="cn-card__action" onClick={onScheduling}>Send scheduling link</button>}
            </div>
            {activeClosed && <div className="cn-inline-msg">This group is closed. Use + New text to start a new one.</div>}
            <div className="cn-texts__msgs">
              {(active.messages || []).map(m => (
                <div key={m.id} className={`cn-bubble-wrap cn-bubble-wrap--${m.direction}`}>
                  <div className="cn-bubble__cap">{m.sender_name || m.from_role} · {fmtMonDay(m.created_at)} {timeOf(m.created_at)}</div>
                  <div className={`cn-bubble cn-bubble--${m.direction}`}>
                    {m.body && <div>{m.body}</div>}
                    {(m.media || []).map((url, i) => <img key={i} className="cn-sms__media" src={url} alt="attachment" />)}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="cn-texts__new">
            <div className="cn-texts__title">New text</div>
            <div className="cn-sms__recipients">
          <label className="cn-check">
            <input type="checkbox" checked={includeAthlete && athleteHasPhone}
              disabled={!athleteHasPhone}
              onChange={e => setIncludeAthlete(e.target.checked)} />
            {' '}{athleteName || 'Athlete'}{athleteHasPhone ? '' : ' (no phone on file)'}
          </label>
          {guardians.map(g => (
            <label className="cn-check" key={g.slot}>
              <input type="checkbox" checked={selectedSlots.includes(g.slot)}
                onChange={() => toggleSlot(g.slot)} /> {g.name}
            </label>
          ))}
          {owner.name && (
            <label className="cn-check">
              <input type="checkbox" checked disabled /> {owner.name} (concierge owner)
            </label>
          )}
          {!owner.textable && owner.blockMessage && (
            <div className="cn-inline-msg cn-inline-msg--err">{owner.blockMessage}</div>
          )}
          {includeCellVisible && (
            <label className="cn-check">
              <input type="checkbox" checked={includeCell}
                onChange={e => setIncludeCell(e.target.checked)} /> Include my cell
            </label>
          )}
          {recipError && (
            <div className="cn-inline-msg cn-inline-msg--err">Couldn't load recipients. Reload the page to try again.</div>
          )}
            </div>
          </div>
        )}
        <div className="cn-texts__composer">
          <textarea className="cn-textarea" rows={2} placeholder="Type a message…" disabled={composerOff}
            value={draft} onChange={e => { setDraft(e.target.value); if (err) setErr(null); }} />
        {fileName && (
          <div className="cn-sms__attachment">
            <span className="cn-sms__attachment-name">
              {uploading ? `Uploading ${fileName}…` : fileName}
            </span>
            <button type="button" className="cn-btn cn-btn--sm" onClick={clearFile} disabled={uploading}>
              Remove
            </button>
          </div>
        )}
          <div className="cn-texts__foot">
            <label className="cn-texts__attach">📎 Attach image
              <input type="file" accept="image/*" onChange={handleFileChange} disabled={composerOff || uploading} style={{ display: 'none' }} />
            </label>
            <span className="cn-texts__hint">Logged to Activity automatically</span>
            <button type="button" className="cn-pbtn"
              disabled={sending || uploading || composerOff || (!draft.trim() && !mediaUrl)}
              onClick={startOrSend}>{active ? 'Send' : 'Start text'}</button>
          </div>
          {err && <div className="cn-inline-msg cn-inline-msg--err">{err}</div>}
        </div>
      </div>
    </section>
  );
}
